import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import VideoCard from '../components/VideoCard';
import { Button } from '../components/ui/button';
import { Loader2, Heart, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Favorites = () => {
  const navigate = useNavigate();
  const { isAuthenticated, getAuthHeader } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchFavorites();
  }, [isAuthenticated]);

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/user/favorites`, getAuthHeader());
      setItems(response.data.items || []);
    } catch (error) {
      console.error('Error fetching favorites:', error);
      toast.error('Failed to load favorites');
    } finally {
      setLoading(false);
    }
  };

  const removeItem = async (videoId) => {
    try {
      await axios.delete(`${API}/user/favorites/${videoId}`, getAuthHeader());
      setItems(items.filter(item => item.video_id !== videoId));
      toast.success('Removed from favorites');
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen pt-20 px-4 md:px-8 lg:px-16 py-8" data-testid="favorites-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Heart className="w-10 h-10 text-red-500 fill-red-500" />
          <h1 
            className="text-4xl md:text-5xl font-normal tracking-wide uppercase"
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
          >
            My Favorites
          </h1>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-favorites">
            <Heart className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg mb-4">No favorites yet</p>
            <p className="text-gray-600 text-sm mb-6">Videos you favorite will appear here</p>
            <Button onClick={() => navigate('/')} data-testid="browse-btn">
              Browse Videos
            </Button>
          </div>
        ) : (
          <>
            <p className="text-gray-400 mb-6">{items.length} favorites</p>
            <div className="video-grid" data-testid="favorites-grid">
              {items.map((item, index) => (
                <div key={item.video_id || index} className="relative group">
                  <VideoCard 
                    video={{
                      id: item.video_id,
                      title: item.title,
                      thumbnail: item.thumbnail,
                      duration: item.duration,
                      views: '-'
                    }} 
                  />
                  <button
                    onClick={() => removeItem(item.video_id)}
                    className="absolute top-2 left-2 p-2 bg-red-500/80 backdrop-blur rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                    data-testid={`remove-favorite-${item.video_id}`}
                  >
                    <Trash2 className="w-4 h-4 text-white" />
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Favorites;
